import axios from "axios"
import { ServiceError } from "@core/exceptions"
import {
    CODE_FORFAIT_ATTEINT,
    messageRefusForfait,
    signalerRefusForfait,
} from "@core/composables/plafond-forfait.helper"

/**
 * Corps d'erreur rendu par les API Homeys. Les champs existent au premier niveau
 * ou sous `detail` selon l'API appelée — d'où la lecture aux deux endroits.
 */
type ApiErrorPayload = {
    message?: string
    code?: string
    description?: string
    details?: unknown
    context?: unknown
}

/**
 * Fonction permettant de gérer la logique d'appel d'une API et de gérer les erreurs qui peuvent survenir.
 * Si une erreur survient, elle est transformée en `ServiceError` pour être traitée plus facilement dans les composants.
 *
 * Le mapping suit le contrat documenté sur `ServiceError` : `description` porte
 * l'explication que l'API a rédigée POUR l'utilisateur, et le message technique
 * d'axios (« Request failed with status code 502 ») va dans `technicalMessage`,
 * qui ne doit jamais être affiché (DEV-1088).
 *
 * Ceci est un exemple d'utilisation lors de l'appel d'une API :
 * ```typescript
 * handleServiceCall(async () => {
 *    const { data } = await axios.get("/api/gateway/auth/verify-key", {
 *       params: {
 *         type: "data",
 *      },
 *     headers: {
 *      "x-widget-key": key,
 *    },
 * })
 * ```
 *
 * @param serviceCall - Fonction d'appel de l'API
 * @returns
 */
export async function handleServiceCall<T>(
    serviceCall: () => Promise<T>
): Promise<T> {
    return serviceCall().catch((error) => {
        if (axios.isCancel(error)) {
            throw error
        }
        console.error(error)
        if (error.response) {
            const payload: ApiErrorPayload & { detail?: ApiErrorPayload } =
                error.response.data ?? {}
            const detail: ApiErrorPayload = payload.detail ?? {}
            const serviceError = new ServiceError(
                {
                    message:
                        (detail.message ? detail.message : payload.message) ??
                        "Une erreur est survenue",
                    code:
                        (detail.code ? detail.code : payload.code) ?? "unknown",
                    details: detail.details
                        ? detail.details
                        : (payload.details ?? undefined),
                    description:
                        (detail.description
                            ? detail.description
                            : payload.description) ?? undefined,
                    technicalMessage: error.message,
                    context: detail.context ?? payload.context,
                },
                error.response.status
            )
            // Refus du forfait à l'ajout (DEV-2254) : les formulaires d'ajout
            // l'affichent avec les nombres servis, au lieu d'une erreur générique.
            if (serviceError.code === CODE_FORFAIT_ATTEINT) {
                // Le message brut de l'API est le code : les composants qui
                // affichent `message` montreraient « forfait_atteint ».
                const refus = signalerRefusForfait(serviceError)
                if (refus) {
                    const texte = messageRefusForfait(refus).texte
                    serviceError.description ??= texte
                    serviceError.message = texte
                }
            }
            throw serviceError
        } else {
            throw new ServiceError(
                {
                    message: "Une erreur inconnue est survenue",
                    code: "UNKNOWN",
                    technicalMessage: error.message,
                },
                500
            )
        }
    })
}
